import { useState, useEffect } from 'react'
import { createSubscription } from '../utils/actioncable'
import styles from './WorkspaceDeletionProgress.module.css'

export default function WorkspaceDeletionProgress({ 
    workspace, 
    userId, 
    onComplete, 
    onError, 
    onCancel,
    isCompleted = false
}) {
    const [progress, setProgress] = useState(isCompleted ? 100 : 0)
    const [message, setMessage] = useState(isCompleted ? 'Workspace deleted successfully!' : 'Initializing deletion...')
    const [deletedCount, setDeletedCount] = useState(0)
    const [totalCount, setTotalCount] = useState(0)
    const [internalCompleted, setInternalCompleted] = useState(isCompleted)
    const [hasError, setHasError] = useState(false)

    useEffect(() => {
        // Don't create subscription if already completed
        if (isCompleted) return

        // Subscribe to the workspace deletion channel
        const subscription = createSubscription({
            channel: 'WorkspaceDeletionChannel',
            workspace_id: workspace.id
        }, {
            received: (data) => {
                console.log('🔄 Workspace deletion WebSocket message:', data);
                switch (data.type) {
                    case 'deletion_started':
                        console.log('🚀 Deletion started');
                        setMessage(data.message)
                        setProgress(0)
                        break
                        
                    case 'progress_update':
                        console.log('📊 Progress update:', data.progress + '%');
                        setMessage(data.message)
                        setProgress(data.progress)
                        setDeletedCount(data.deleted_count)
                        setTotalCount(data.total_count)
                        break
                        
                    case 'deletion_completed':
                        console.log('✅ Deletion completed - keeping modal open');
                        setMessage(data.message)
                        setProgress(100)
                        setInternalCompleted(true)
                        // Don't auto-call onComplete - let user close manually
                        // onComplete will be called when user clicks "Close" button
                        break
                        
                    case 'deletion_failed':
                        console.log('❌ Deletion failed:', data.message);
                        setMessage(data.message)
                        setHasError(true)
                        onError && onError(data)
                        break
                }
            },
            
            connected: () => {
                console.log('Connected to workspace deletion channel')
            },
            
            disconnected: () => {
                console.log('Disconnected from workspace deletion channel')
            }
        })

        // Cleanup on unmount
        return () => {
            subscription?.unsubscribe()
        }
    }, [workspace.id, userId, onComplete, onError, isCompleted])

    const formatProgress = () => {
        if (totalCount > 0) {
            return `${deletedCount} / ${totalCount} records`
        }
        return `${Math.round(progress)}%`
    }

    const getProgressBarClass = () => {
        if (hasError) return `${styles.progressBar} ${styles.error}`
        if (isCompleted || internalCompleted) return `${styles.progressBar} ${styles.completed}`
        return styles.progressBar
    }

    return (
        <div className={styles.progressContainer}>
            <div className={styles.header}>
                <h3>{(isCompleted || internalCompleted) ? `Workspace Deleted: ${workspace.name}` : `Deleting Workspace: ${workspace.name}`}</h3>
                {!(isCompleted || internalCompleted) && !hasError && (
                    <button 
                        onClick={onCancel}
                        className={styles.cancelButton}
                        title="Cancel deletion"
                    >
                        ✕
                    </button>
                )}
            </div>

            <div className={styles.progressSection}>
                <div className={styles.progressBarContainer}>
                    <div 
                        className={getProgressBarClass()}
                        style={{ width: `${progress}%` }}
                    />
                </div>
                
                <div className={styles.progressText}>
                    {formatProgress()}
                </div>
            </div>

            <div className={styles.messageSection}>
                <div className={styles.currentMessage}>
                    {message}
                </div>
                
                {hasError && (
                    <div className={styles.errorActions}>
                        <button 
                            onClick={onError}
                            className={styles.retryButton}
                        >
                            Try Again
                        </button>
                        <button 
                            onClick={onCancel}
                            className={styles.cancelButton}
                        >
                            Cancel
                        </button>
                    </div>
                )}
                
                {(isCompleted || internalCompleted) && (
                    <div className={styles.successSection}>
                        <div className={styles.successMessage}>
                            ✅ Workspace "{workspace.name}" has been successfully deleted!
                        </div>
                        <div className={styles.successDetails}>
                            <p>All data has been permanently removed:</p>
                            <ul>
                                <li>Projects and tasks</li>
                                <li>Time logs and worklogs</li>
                                <li>Clients and groups</li>
                                <li>User access permissions</li>
                                <li>Workspace settings</li>
                            </ul>
                        </div>
                        <button 
                            onClick={() => onComplete && onComplete()}
                            className={styles.closeButton}
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.warningText}>
                <small>
                    ⚠️ This operation cannot be undone. All data associated with this workspace will be permanently deleted.
                </small>
            </div>
        </div>
    )
}